/**
 * voronoiCrystalPipeline — 3D-faceted voronoi cells with internal
 * dancing caustics + iridescent edges.
 *
 * The plane is tessellated by a 3-octave voronoi field; each cell is
 * shaded as if it were a translucent crystal: a depth-based normal
 * (computed from the gradient of the field) drives a refraction-like
 * sample of the iridescent palette, and an internal caustic made of
 * 3 intersecting travelling sine waves rises from the cell center.
 *
 * Cell edges glow with a thin-film iridescent band whose hue depends
 * on the edge angle relative to the cell's pseudo-depth.
 *
 * Audio is mapped onto the caustic speed, the edge glow intensity,
 * and a global dispersion amount.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface VoronoiCrystalPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)),
           dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// 3x3 voronoi: returns (cellId.xy, distance, edgeDistance).
// edgeDistance is distance to the closest of (F1-F2) differences across
// the 8 neighbors, which gives a clean edge field.
vec4 voronoi3(vec2 p) {
  vec2 n = floor(p);
  vec2 f = fract(p);
  float F1 = 8.0;
  float F2 = 8.0;
  vec2 nearest = vec2(0.0);
  vec2 second  = vec2(0.0);
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 g = vec2(float(i), float(j));
      vec2 o = hash22(n + g);
      vec2 r = g + o - f;
      float d = dot(r, r);
      if (d < F1) {
        F2 = F1; second = nearest;
        F1 = d; nearest = n + g;
      } else if (d < F2) {
        F2 = d; second = n + g;
      }
    }
  }
  // Approximate edge distance via F2-F1 (small near a border).
  float edge = sqrt(F2) - sqrt(F1);
  return vec4(nearest, sqrt(F1), edge);
}

// Compute gradient of the field via finite difference.
vec2 voronoiGrad(vec2 p) {
  float e = 0.005;
  vec4 a = voronoi3(p + vec2(e, 0.0));
  vec4 b = voronoi3(p - vec2(e, 0.0));
  vec4 c = voronoi3(p + vec2(0.0, e));
  vec4 d = voronoi3(p - vec2(0.0, e));
  return vec2(a.z - b.z, c.z - d.z) / (2.0 * e);
}

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime;

  // Background: deep purple/violet with subtle nebula haze.
  vec3 bg = vec3(0.05, 0.025, 0.08);
  // A 2D coordinate scaled to camera coords.
  vec2 nuv = p * 1.8 + vec2(t * 0.02, -t * 0.015);
  bg += vec3(0.06, 0.04, 0.09) * noise2(nuv * 2.0);

  // Voronoi field driven by a slow scaling.
  float scale = 5.5 - bass * 0.4 + 0.2 * sin(t * 0.5);
  vec2 q = p * scale + vec2(t * 0.05, t * 0.08);

  vec4 v = voronoi3(q);
  float dCell = v.z;
  float edge  = v.w; // (sqrt(F2) - sqrt(F1)) ~ 0 on borders

  // Pseudo-depth per cell: hash-based, slow oscillation.
  float cellDepth = 0.5 + 0.5 * sin(
    t * 0.8
    + hash21(v.xy) * 30.0
    + hash21(vec2(v.x, 0.0)) * 4.0);

  // Edge mask — high near cell borders.
  float edgeMask = 1.0 - smoothstep(0.0, 0.10, edge);
  edgeMask = pow(edgeMask, 1.6);

  // Per-cell color seed.
  float cellSeed = hash21(v.xy);

  // Internal caustic: 3 traveling sine waves whose phase comes from the
  // cell-seed + time, plus an audio boost.
  vec2 lc = p - (v.xy + 0.5) / scale;
  float lcScale = length(lc);
  float wave1 = sin(lc.x * 28.0 + t * (1.0 + bass * 1.5) + cellSeed * 13.0);
  float wave2 = sin(lc.y * 22.0 + t * (1.3 + mid  * 1.2) + cellSeed * 21.0);
  float wave3 = sin((lc.x + lc.y) * 18.0 + t * (0.9 + high * 1.8) + cellSeed * 31.0);
  float caust = 0.5 + 0.5 * (wave1 + wave2 + wave3) / 3.0;
  caust = pow(caust, 3.5);
  // Caustic only inside cells, near edge it dims.
  float insideMask = smoothstep(0.07, 0.04, edge);

  // Cell face color: deep crystal with iridescent caustics.
  vec3 cellColA = vec3(0.25, 0.10, 0.55);   // amethyst
  vec3 cellColB = vec3(0.10, 0.30, 0.55);   // teal
  vec3 cellColC = vec3(0.65, 0.20, 0.40);   // rose
  vec3 cellBase = mix(cellColA, cellColB, cellSeed);
  cellBase = mix(cellBase, cellColC, 0.5 + 0.5 * sin(cellSeed * 12.0 + t * 0.4));

  // Mixing iridescent caustic color into the cell.
  vec3 caustCol = mix(vec3(1.0, 0.85, 0.30),
                      vec3(0.10, 0.95, 0.95),
                      0.5 + 0.5 * sin(t + cellSeed * 6.0));
  vec3 cellShade = cellBase
    + caustCol * caust * insideMask * (0.6 + bass * 0.8 + mid * 0.4);

  // Background visible inside the cell face.
  vec3 col = mix(bg, cellShade, 0.90);

  // Edge glow: iridescent band.
  vec3 edgeColA = vec3(1.0, 0.40, 0.85);
  vec3 edgeColB = vec3(0.40, 1.0, 0.85);
  vec3 edgeColC = vec3(0.70, 0.50, 1.0);
  vec3 edgeCol = mix(edgeColA, edgeColB, 0.5 + 0.5 * sin(cellSeed * 7.0 + t));
  edgeCol = mix(edgeCol, edgeColC, 0.5 + 0.5 * cos(cellSeed * 4.0 + t * 0.7));

  float edgeBoost = (0.55 + 1.5 * (mid + high * 0.7));
  col += edgeCol * edgeMask * edgeBoost;

  // Pseudo-specular: bright highlight where gradient + cell-seed align.
  vec2 grad = voronoiGrad(q);
  float spec = pow(clamp(1.0 - length(grad), 0.0, 1.0), 3.0);
  col += vec3(1.0, 0.95, 0.80) * spec * (0.35 + high * 1.5);

  // Internal dispersion: shift hue based on cellDepth + audio.
  float hueShift = cellDepth + bass * 0.6 + high * 0.4;
  vec3 disp = vec3(
    sin(hueShift * 6.28 + 0.0),
    sin(hueShift * 6.28 + 2.1),
    sin(hueShift * 6.28 + 4.2)
  ) * 0.5 + 0.5;
  col = mix(col, col * disp, 0.20 * (0.5 + high * 0.6));

  // Global audio tint.
  col += vec3(0.08, 0.04, 0.18) * bass * 0.30
       + vec3(0.10, 0.18, 0.10) * mid  * 0.20
       + vec3(0.22, 0.10, 0.20) * high * 0.15;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeVoronoiCrystalBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.2);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.7 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.4 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.4 + 2.1);
}

export function computeVoronoiCrystalRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createVoronoiCrystalPipeline(gl: WebGL2RenderingContext): VoronoiCrystalPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawVoronoiCrystal(
  gl: WebGL2RenderingContext,
  pipe: VoronoiCrystalPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
